import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import {
  Box,
  Chip,
  Button,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import { t } from 'i18next';
import { paths } from '@common/types';
import { useGeneral } from './hooks';
import { ImageUploader } from 'components/ImageUploader';
import { domainRegex } from '@common/schema';
import { USStateSelect } from 'components/USStateSelect';
import { FormGooglePlacesAutocomplete, SaveChangesModal } from '@components';

interface GeneralProps {
  organization: paths[`/organization/{record_id}`]['get']['responses']['200']['content']['application/json'];
}

export const General = ({ organization }: GeneralProps) => {
  const {
    formMethods,
    onSubmit,
    handleAddDomain,
    handleRemoveDomain,
    isSubmitEnabled,
    logo,
    handleUploadLogo,
    handleDeleteLogo,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useGeneral(organization);

  const { control, watch } = formMethods;

  const currentDomains = watch('domains');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            backgroundColor: 'white',
            position: 'relative',
            gap: '0.5rem',
          }}
        >
          <ImageUploader
            croppedImage={logo}
            setCroppedImage={handleUploadLogo}
            onDelete={handleDeleteLogo}
          />

          <Controller
            name='name'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.organizations.addModal.name')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                sx={{ paddingBottom: '0.5rem' }}
                required
                fullWidth
              />
            )}
          />
          <Controller
            name='tin'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.organizations.addModal.tin')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Controller
            name='guide_participant_id'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t(
                  'adminPanel.organizations.addModal.guide_participant_id',
                )}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Box sx={{ width: '100%' }}>
            <Controller
              name='domainGenerator'
              control={control}
              render={({ field, fieldState: { error } }) => {
                const isAddDisabled =
                  !field.value ||
                  !domainRegex.test(field.value || '') ||
                  currentDomains.includes(field.value || '');

                return (
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel
                      htmlFor={'domainGenerator'}
                      error={!isNil(error)}
                      required
                    >
                      {t(
                        'adminPanel.organizations.addModal.organizationEmailDomains',
                      )}
                    </InputLabel>
                    <OutlinedInput
                      {...field}
                      type='text'
                      label='Organization email domains'
                      error={!isNil(error)}
                      required
                      fullWidth
                      sx={{ color: 'black' }}
                      endAdornment={
                        <InputAdornment position='end'>
                          <Button
                            variant='outlined'
                            sx={{ paddingY: 0 }}
                            onClick={() => handleAddDomain(field.value)}
                            disabled={isAddDisabled}
                          >
                            CONFIRM
                          </Button>
                        </InputAdornment>
                      }
                    />
                    {!!error?.message && (
                      <FormHelperText error id='domainGenerator-error'>
                        {error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                );
              }}
            />
            <Controller
              name='domains'
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormControl fullWidth sx={{ padding: 0 }}>
                    <Box
                      sx={{
                        overflow: 'auto',
                        maxHeight: '3rem',
                        height: 'fit-content',
                        pt: '0.5rem',
                      }}
                    >
                      {(field.value ?? []).map((domain, index) => (
                        <Chip
                          key={`${domain}-${index}`}
                          label={`@${domain}`}
                          onDelete={() => handleRemoveDomain(index)}
                          sx={{
                            borderRadius: '1rem',
                            mx: '0.25rem',
                            mb: '0.5rem',
                          }}
                        />
                      ))}
                      {!!error?.message && (
                        <FormHelperText error id='domainGenerator-error'>
                          {error.message}
                        </FormHelperText>
                      )}
                    </Box>
                  </FormControl>
                );
              }}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              gap: '1rem',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              {t('adminPanel.organizations.addModal.contactInformation')}
            </Typography>

            <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='street'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.organizations.addModal.addressLine1')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='suite'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.organizations.addModal.addressLine2')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='city'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.organizations.addModal.city')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    required
                  />
                )}
              />
            </Box>
            <Controller
              name='zip_code'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.organizations.addModal.zip')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='county'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.organizations.addModal.county')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              pt: '0.5rem',
              position: 'sticky',
              bottom: '0',
              left: 0,
              bgcolor: theme => theme.palette.common.white,
              zIndex: 10,
            }}
          >
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={!isSubmitEnabled}
              loading={isEditSubmitting}
              loadingPosition='center'
            >
              {t('general.saveChanges')}
            </Button>
          </Box>
        </Form>
      </FormProvider>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Box>
  );
};
